import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatStepperModule } from '@angular/material/stepper';
import { ProjectService } from '../../../services/project.service';
import { MatIconModule } from '@angular/material/icon';
import { vm_tag } from '../../../models/view-models/vm-tags.model';
import { vm_objective } from '../../../models/view-models/vm-projects.model';
import { ViewModelMapperService } from '../../../services/view-model.mapper.service';
import { ApiService } from '../../../services/api.service';
import { IconComponent } from '../../../ui-component-library/core/icon.component/icon.component';
import { ComponentType } from '../../../models/core-models/enums';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-tags',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatStepperModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatButtonModule,
    MatIconModule,
    IconComponent,
    MatTooltipModule,
  ],
  templateUrl: './tags.component.html',
  styleUrl: './tags.component.css',
})
export class TagsComponent implements OnInit {
  private fb = inject(FormBuilder);

  objectives: vm_objective[] = [];
  tags: vm_tag[] = [];
  tagsForm!: FormGroup;
  isEditMode = false;

  constructor(
    private viewModelMapper: ViewModelMapperService,
    private apiService: ApiService,
    private projectSrvc: ProjectService
  ) {
    this.apiService.dataChangeEvent$.subscribe((data) => {
      if (data.dataLoaded) {
        this.loadInitialData();
      }
    });
  }

  ngOnInit() {
    this.loadInitialData();
  }

  loadInitialData() {
    this.tags = this.viewModelMapper
      .mapTagsToViewModel(
        this.apiService.tags,
        this.apiService.tagControls,
        this.apiService.icons,
        this.apiService.appComponent
      )
      .filter((tag) => tag?.type?.includes(ComponentType.Projects));
    this.objectives = this.viewModelMapper.mapObjectivesToViewModel(this.apiService.objectives);
    this.initForm();
    this.loadExistingData();
  }

  initForm() {
    // Only initialize when both objectives and tags are loaded

    this.tagsForm = this.fb.group({
      objectives: this.fb.array(this.objectives.map(() => false)),
      tags: this.fb.array(this.tags.map(() => false)),
    });
  }

  onObjectiveChange(index: number) {
    const objectivesArray = this.tagsForm.get('objectives') as FormArray;
    const isSelected = objectivesArray.at(index).value;
    objectivesArray.at(index).setValue(isSelected);
    this.objectives[index].selected = isSelected;
  }

  onTagChange(index: number) {
    const tagsArray = this.tagsForm.get('tags') as FormArray;
    const isSelected = tagsArray.at(index).value;
    tagsArray.at(index).setValue(isSelected);
    this.tags[index].selected = isSelected;
  }

  getFullData() {
    const selectedObjectives = this.objectives?.filter((obj, idx) => this.tagsForm?.value.objectives[idx]);
    const selectedTags = this.tags?.filter((tag, idx) => this.tagsForm?.value.tags[idx]);
    return {
      objectives: selectedObjectives,
      tags: selectedTags,
    };
  }

  loadDataForEdit(): void {
    this.isEditMode = true;
    // this.loadExistingData();
  }

  private loadExistingData(): void {
    const existingData = this.projectSrvc.getProjectFormData().tags;
    if (existingData && this.tagsForm) {
      // Update objective selections
      if (existingData.objectives) {
        existingData.objectives.forEach((existingObj: any) => {
          if (existingObj.selected) {
            const objIndex = this.objectives.findIndex(
              (obj) => obj.id === existingObj.id || obj.id === existingObj.id_objective
            );
            if (objIndex !== -1) {
              const objectivesArray = this.tagsForm.get('objectives') as FormArray;
              objectivesArray.at(objIndex).setValue(true);
              this.objectives[objIndex].selected = true;
            }
          }
        });
      }

      // Update tag selections
      if (existingData.tags) {
        existingData.tags.forEach((existingTag: any) => {
          if (existingTag.selected) {
            const tagIndex = this.tags.findIndex((tag) => tag.id === existingTag.id || tag.id === existingTag.id);
            if (tagIndex !== -1) {
              const tagsArray = this.tagsForm.get('tags') as FormArray;
              tagsArray.at(tagIndex).setValue(true);
              this.tags[tagIndex].selected = true;
            }
          }
        });
      }
    }
  }
}
